import React, { useState, useEffect, useMemo } from 'react';
import syncService from '../../services/syncService';
import Spinner from './Spinner';
import SyncProjectPicker from './SyncProjectPicker';
import './ConfirmModal.css';
import './CopyConversionsModal.css';

// Copy task settings from the same task on another sync project — Configure Source / Configure Target.
//
// THE ONE RULE THAT DIFFERS FROM CONVERSIONS: here a setting that already has a value CAN be replaced.
// A conversion is a fact about this property's data and the other project has no standing to overwrite
// it; a connectivity setting is configuration you are deliberately copying because the other property
// is set up the way you want this one. So overrides are offered — but they start UNTICKED and are
// listed apart from the additions, because "fill in the blanks" and "change what is already working"
// are different decisions and should not be made with one click.
//
// NOTHING IS SAVED HERE. Copying writes into the form on the screen behind this modal; the user then
// reviews the fields and presses Save, which goes through the ordinary save path (role, api key,
// inherited credential and all). A modal that wrote straight to the server would be a second save path
// to keep in step with the first, and would rob the user of the look before committing.

// PASSWORD settings are shown as a fixed mask, never as characters. The value is real — connectivity
// settings are decrypted for this form — but a copy dialog is not a reason to put a client secret on
// screen. What the user needs to know is whether it is set and whether it differs, and both survive
// masking.
const MASK = '••••••••';
const isSecret = (s) => String(s?.dataType || '').toUpperCase() === 'PASSWORD';
const shown = (setting, value) => {
    const v = value == null ? '' : String(value);
    if (!v) return '';
    return isSecret(setting) ? MASK : v;
};
const norm = (v) => String(v == null ? '' : v).trim();

const CopySettingsModal = ({
    accountId,
    currentSyncUuid,
    templateName,
    taskName,                 // 'CONFIGURE SOURCE' | 'CONFIGURE TARGET' — the task read on the other project
    settings = [],            // this task's setting definitions (task.taskSettings)
    values = {},              // what is in the form right now — what a copy would replace
    onApply,                  // (picked) => void, picked is { settingName: value }
    onClose,
}) => {
    const [step, setStep]             = useState('project');
    const [selected, setSelected]     = useState(null);
    const [sourceSettings, setSource] = useState(null);
    const [loading, setLoading]       = useState(false);
    const [loadError, setLoadError]   = useState(null);
    const [checked, setChecked]       = useState(() => new Set());
    const [filter, setFilter]         = useState('');

    useEffect(() => {
        const onKey = e => {
            if (e.key !== 'Escape') return;
            if (step === 'review') { setStep('project'); return; }
            onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, step]);

    // Only settings THIS task actually has can be copied — the save path writes `task.taskSettings` and
    // silently drops anything else, so a value with nowhere to land would look copied and never arrive.
    // The ones that do not exist here are counted and reported rather than quietly discarded.
    const mine = useMemo(() => {
        const m = new Map();
        (settings || []).forEach(s => m.set(s.settingName, s));
        return m;
    }, [settings]);

    const diff = useMemo(() => {
        if (!Array.isArray(sourceSettings)) return null;
        const additions = [], overrides = [];
        let same = 0, notHere = 0, empty = 0;
        for (const src of sourceSettings) {
            const def = mine.get(src.settingName);
            if (!def) { notHere++; continue; }
            const theirs = norm(src.settingValue);
            if (!theirs) { empty++; continue; }          // nothing to copy from
            const ours = norm(values[src.settingName]);
            if (ours === theirs) { same++; continue; }
            (ours ? overrides : additions).push({ def, name: src.settingName, ours, theirs });
        }
        return { additions, overrides, same, notHere, empty };
    }, [sourceSettings, mine, values]);

    const loadSource = async (project) => {
        setLoading(true); setLoadError(null); setSource(null); setSelected(project);
        try {
            const details = await syncService.getSyncProjectDetails(project.syncuuid);
            const task = (details.projectTasks || []).find(t => t.taskName === taskName);
            if (!task) { setLoadError(`This project has no "${taskName}" task.`); return; }
            const data = await syncService.getTaskDetails(project.syncuuid, task.syncProjectTaskUuid);
            setSource(Array.isArray(data?.taskSettings) ? data.taskSettings : []);
        } catch {
            setLoadError('The settings for this project could not be loaded.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelect = (p) => {
        if (selected?.syncuuid === p.syncuuid) { setSelected(null); setSource(null); setLoadError(null); return; }
        loadSource(p);
    };

    // ADDITIONS START TICKED, OVERRIDES DO NOT. Filling an empty field is what the user came for;
    // replacing a value that is already working is a decision they should have to make on purpose.
    const goToReview = () => {
        if (!diff) return;
        setChecked(new Set(diff.additions.map(r => r.name)));
        setFilter('');
        setStep('review');
    };

    const rows = useMemo(() => {
        if (!diff) return [];
        return [...diff.additions.map(r => ({ ...r, kind: 'add' })),
                ...diff.overrides.map(r => ({ ...r, kind: 'override' }))];
    }, [diff]);

    const visible = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            r.name.toLowerCase().includes(q) ||
            String(r.def.settingLabel || '').toLowerCase().includes(q) ||
            String(r.def.subGroup || '').toLowerCase().includes(q));
    }, [rows, filter]);

    const toggle = (name) => setChecked(prev => {
        const next = new Set(prev);
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });

    const allVisibleChecked = visible.length > 0 && visible.every(r => checked.has(r.name));
    const toggleAllVisible = () => setChecked(prev => {
        const next = new Set(prev);
        visible.forEach(r => { if (allVisibleChecked) next.delete(r.name); else next.add(r.name); });
        return next;
    });

    const chosen = rows.filter(r => checked.has(r.name));
    const overridesChosen = chosen.filter(r => r.kind === 'override').length;

    const apply = () => {
        const picked = {};
        chosen.forEach(r => { picked[r.name] = r.theirs; });
        onApply(picked, selected);
    };

    const canReview = !!selected && !loading && !loadError && !!diff && rows.length > 0;

    return (
        <div className="cm-backdrop" onClick={onClose}>
            <div className="cm-dialog ccm-dialog" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">

                {step === 'project' && (
                    <>
                        <h2 className="cm-title">Copy settings &mdash; choose a project</h2>
                        <p className="ccm-hint">
                            Step 1 of 2. Settings are read from the same <strong>{taskName}</strong> task on the project
                            you pick. Nothing is copied or saved yet.
                        </p>

                        <SyncProjectPicker
                            accountId={accountId}
                            currentSyncUuid={currentSyncUuid}
                            templateName={templateName}
                            selected={selected}
                            busy={loading}
                            onSelect={handleSelect}
                            renderBadge={() => (
                                loading ? <Spinner size="sm" />
                                : loadError ? <span className="ccm-badge ccm-badge--grey">Could not load</span>
                                : diff ? (
                                    <span className={`ccm-badge ${rows.length ? 'ccm-badge--green' : 'ccm-badge--grey'}`}>
                                        {rows.length ? `${rows.length} to review` : 'nothing to copy'}
                                    </span>
                                ) : null
                            )}
                        />

                        {selected && !loading && (loadError || diff) && (
                            <div className={`ccm-summary ${canReview ? 'ccm-summary--green' : 'ccm-summary--amber'}`}>
                                {loadError ? loadError
                                    : rows.length === 0
                                        ? `"${selected.projectName}" has no settings that differ from this one.`
                                        : `${diff.additions.length} to fill in`
                                          + (diff.overrides.length ? ` · ${diff.overrides.length} would override` : '')
                                          + (diff.same ? ` · ${diff.same} already identical` : '')
                                          + (diff.notHere ? ` · ${diff.notHere} not on this connector` : '')}
                            </div>
                        )}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={onClose}>Cancel</button>
                            <button className="cm-btn-confirm" onClick={goToReview} disabled={!canReview}>
                                {canReview ? `Review ${rows.length} setting${rows.length !== 1 ? 's' : ''}` : 'Review'}
                            </button>
                        </div>
                    </>
                )}

                {step === 'review' && diff && (
                    <>
                        <h2 className="cm-title">Copy settings &mdash; choose what to apply</h2>
                        <p className="ccm-hint">
                            Step 2 of 2. Ticked settings are written into the form &mdash; <strong>nothing is saved
                            until you press Save</strong> on the task. Overrides start unticked.
                        </p>

                        <div className="ccm-review-bar">
                            <label className="ccm-selectall">
                                <input type="checkbox" checked={allVisibleChecked} onChange={toggleAllVisible} />
                                <span>{allVisibleChecked ? 'Deselect' : 'Select'} all{filter ? ' shown' : ''}</span>
                            </label>
                            <input
                                className="ccm-search ccm-search--inline"
                                type="text"
                                placeholder="Filter..."
                                value={filter}
                                onChange={e => setFilter(e.target.value)}
                            />
                            <span className="ccm-count">{chosen.length} of {rows.length} selected</span>
                        </div>

                        <div className="ccm-list ccm-difflist ccm-difflist--settings">
                            <div className="ccm-diffhead">
                                <span />
                                <span className="ccm-cell--type">Setting</span>
                                <span className="ccm-cell--from">Here now</span>
                                <span />
                                <span className="ccm-cell--to">From {selected.projectName}</span>
                            </div>
                            {visible.length === 0 && (
                                <div className="ccm-empty">No settings match "{filter}".</div>
                            )}
                            {visible.map(r => (
                                <label
                                    key={r.name}
                                    className={`ccm-diffrow${checked.has(r.name) ? ' ccm-diffrow--on' : ''}${r.kind === 'override' ? ' ccm-diffrow--override' : ''}`}
                                >
                                    <input type="checkbox" checked={checked.has(r.name)} onChange={() => toggle(r.name)} />
                                    <span className="ccm-cell ccm-cell--type" title={r.name}>
                                        {r.def.settingLabel || r.name}
                                        {r.kind === 'override' && <span className="ccm-tag-override">override</span>}
                                    </span>
                                    <span className="ccm-cell ccm-cell--from" title={isSecret(r.def) ? '' : r.ours}>
                                        {r.ours ? shown(r.def, r.ours) : <em className="ccm-blank">not set</em>}
                                    </span>
                                    <span className="ccm-cell ccm-cell--arrow" aria-hidden="true">&rarr;</span>
                                    <span className="ccm-cell ccm-cell--to" title={isSecret(r.def) ? '' : r.theirs}>
                                        {shown(r.def, r.theirs)}
                                    </span>
                                </label>
                            ))}
                        </div>

                        {overridesChosen > 0 && (
                            <div className="ccm-summary ccm-summary--amber">
                                {overridesChosen} setting{overridesChosen !== 1 ? 's' : ''} already
                                {overridesChosen !== 1 ? ' have' : ' has'} a value here and will be replaced.
                            </div>
                        )}

                        {diff.notHere > 0 && (
                            <p className="ccm-hint">
                                {diff.notHere} setting{diff.notHere !== 1 ? 's' : ''} on that project
                                {diff.notHere !== 1 ? ' do' : ' does'} not exist on this connector and cannot be copied.
                            </p>
                        )}

                        <div className="cm-actions">
                            <button className="cm-btn-cancel" onClick={() => setStep('project')}>Back</button>
                            <button className="cm-btn-confirm" onClick={apply} disabled={!chosen.length}>
                                Copy {chosen.length} setting{chosen.length !== 1 ? 's' : ''} into the form
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CopySettingsModal;
